import React, { useState, useEffect, useRef } from 'react';
import {
    Button,
    DatePicker,
    Form,
    Input,
    InputNumber,
    Select,
    Switch,
    Col,
    Row,
    Card,
    Space,
    message,
    Tag,
    Typography,
    Divider,
    Statistic,
    Alert,
    Spin,
    Modal,
} from 'antd';
import {
    BankOutlined,
    LinkOutlined,
    CloseCircleOutlined,
    SaveOutlined,
    CloseOutlined,
    DollarOutlined,
    FileTextOutlined,
    UserOutlined,
    CreditCardOutlined,
    CheckCircleOutlined,
    SendOutlined,
    ExclamationCircleOutlined,
    FilePdfOutlined,
    EyeOutlined,
    DownloadOutlined,
    CopyOutlined,
} from '@ant-design/icons';
import { useDispatch } from 'react-redux';
import { downloadReceipt } from 'store/slices/PaymentSlice';
import TextArea from 'antd/lib/input/TextArea';
import * as adminactions from "store/actions/AuthActions";
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { dateFormat } from 'constants/constants';
import dayjs from 'dayjs';
import { useSelector } from 'react-redux';
import BankTransactionSelectModal from '../../components/Common/BankTransactionSelectModal';
import InvoiceSelectModal from '../../components/Common/InvoiceSelectModal';
import { formatMoney } from 'utils/money';
import { copyText } from 'utils/clipboard';

const { Title, Text } = Typography;
const { Option } = Select;

// Status configuration
const STATUS_CONFIG = {
    PENDING: { color: 'orange', icon: <ExclamationCircleOutlined /> },
    PAID: { color: 'green', icon: <CheckCircleOutlined /> },
    REFUNDED: { color: 'purple', icon: <DollarOutlined /> },
    FAILED: { color: 'red', icon: <CloseCircleOutlined /> },
};

const PaymentForm = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [searchParams] = useSearchParams();
    const [form] = Form.useForm();

    // State
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [downloadingReceipt, setDownloadingReceipt] = useState(false);
    const [dataSource, setDataSource] = useState(null);
    const [accounts, setAccounts] = useState([]);
    const [paymentModes, setPaymentModes] = useState([]);
    const [paymentTypes, setPaymentTypes] = useState([]);
    const [selectedPaymentType, setSelectedPaymentType] = useState(undefined);
    const [showSuccess, setShowSuccess] = useState(false);

    // Editable payer details (submitted by the payer on a multi-payment link)
    const [payerEdit, setPayerEdit] = useState({ name: '', email: '', phone: '' });

    // Modal states
    const [bankTxnModalVisible, setBankTxnModalVisible] = useState(false);
    const [selectedBankTxn, setSelectedBankTxn] = useState(null);
    const [invoiceModalVisible, setInvoiceModalVisible] = useState(false);
    const [selectedInvoice, setSelectedInvoice] = useState(null);

    // Payment link states
    const [paymentLinkModalOpen, setPaymentLinkModalOpen] = useState(false);
    const [paymentLinkLoading, setPaymentLinkLoading] = useState(false);
    const [paymentLinkResult, setPaymentLinkResult] = useState(null);

    // Multi-currency states
    const [isForeignCurrency, setIsForeignCurrency] = useState(false);
    const [invoiceCurrency, setInvoiceCurrency] = useState(null);
    const [exchangeRate, setExchangeRate] = useState(null);
    const [exchangeRateLoading, setExchangeRateLoading] = useState(false);
    const [originalAmount, setOriginalAmount] = useState(null);

    const organization = useSelector(state => state.auth.organization);
    const isEditMode = !!id;
    const skipInvoiceEffect = useRef(false);

    // Format currency using invoice currency or default to INR
    const formatCurrency = (amount, currencyCode = null) => {
        const code = currencyCode || (invoiceCurrency?.iso_code) || 'INR';
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: code,
            minimumFractionDigits: 2,
        }).format(amount || 0);
    };

    // Load payment data for edit mode
    // Persist payer detail edits to the linked proof as part of the main save.
    // No-op when the payment isn't from a multi-payment link or nothing changed.
    const savePayerDetailsIfChanged = async () => {
        const pd = dataSource?.payer_details;
        if (!pd?.payment_link_id || !pd?.proof_id) return;

        const changed =
            (payerEdit.name || '').trim() !== (pd.name || '') ||
            (payerEdit.email || '').trim() !== (pd.email || '') ||
            (payerEdit.phone || '').trim() !== (pd.phone || '');
        if (!changed) return;

        const response = await adminactions.updatePaymentLinkProof(
            pd.payment_link_id,
            pd.proof_id,
            {
                customer_name: payerEdit.name.trim(),
                customer_email: payerEdit.email.trim(),
                customer_phone: payerEdit.phone.trim(),
            }
        );
        if (response.status === 200) {
            setDataSource((prev) => prev ? {
                ...prev,
                payer_details: {
                    ...prev.payer_details,
                    name: response.data.customer_name,
                    email: response.data.customer_email,
                    phone: response.data.customer_phone,
                },
            } : prev);
        }
    };

    const loadPayment = async () => {
        if (!id) {
            setDataSource({});
            return;
        }

        setLoading(true);
        try {
            const response = await adminactions.loadPayment(id);
            if (response) {
                setSelectedPaymentType(response.payment_type);
                setDataSource(response);

                // Seed editable payer details from the linked proof, if any
                if (response.payer_details) {
                    setPayerEdit({
                        name: response.payer_details.name || '',
                        email: response.payer_details.email || '',
                        phone: response.payer_details.phone || '',
                    });
                }

                // Set multi-currency state from saved payment data
                if (response.currency_data && response.currency_data.iso_code !== 'INR') {
                    setIsForeignCurrency(true);
                    setInvoiceCurrency(response.currency_data);
                    if (response.exchange_rate) setExchangeRate(parseFloat(response.exchange_rate));
                    if (response.original_amount) setOriginalAmount(parseFloat(response.original_amount));
                }

                // If there's an invoice, fetch its details
                if (response.invoice) {
                    try {
                        // Skip the invoice-change useEffect so it doesn't overwrite saved exchange rate
                        skipInvoiceEffect.current = true;
                        const invoiceResponse = await adminactions.loadInvoice(response.invoice);
                        if (invoiceResponse) {
                            setSelectedInvoice(invoiceResponse);
                        }
                    } catch (err) {
                        console.error('Error fetching invoice:', err);
                    }
                }

                // If there's a bank transaction, set it
                if (response.bank_transaction_data) {
                    setSelectedBankTxn(response.bank_transaction_data);
                }

                // Set form values
                form.setFieldsValue({
                    ...response,
                    payment_date: response.payment_date ? dayjs(response.payment_date) : null,
                });
            }
        } catch (err) {
            console.error('Error loading payment:', err);
            message.error('Failed to load payment data');
        } finally {
            setLoading(false);
        }
    };

    // Load accounts
    const loadAccounts = async () => {
        try {
            const response = await adminactions.loadAccounts(1, '', 1000);
            if (response && response.data) {
                setAccounts(response.data.results || response.data || []);
            }
        } catch (err) {
            console.error('Error loading accounts:', err);
        }
    };

    // Load payment modes
    const loadPaymentModes = async () => {
        try {
            const response = await adminactions.loadPaymentModes();
            if (response) {
                setPaymentModes(response);
            }
        } catch (err) {
            console.error('Error loading payment modes:', err);
        }
    };

    // Load payment types
    const loadPaymentTypes = async () => {
        try {
            const response = await adminactions.loadPaymentTypes();
            if (response) {
                setPaymentTypes(response);
            }
        } catch (err) {
            console.error('Error loading payment types:', err);
        }
    };

    // Handle bank transaction selection
    const handleBankTxnSelect = (txnId, txnData) => {
        setSelectedBankTxn(txnData);
        // Auto-fill amount and date from bank transaction
        if (txnData) {
            const updates = {
                bank_transaction: txnData.id,
            };
            if (txnData.amount) {
                updates.amount = parseFloat(txnData.amount);
            }
            if (txnData.transaction_date) {
                updates.payment_date = dayjs(txnData.transaction_date);
            }
            form.setFieldsValue(updates);
        } else {
            form.setFieldsValue({ bank_transaction: null });
        }
    };

    // Handle invoice selection
    const handleInvoiceSelect = (invoiceId, invoiceData) => {
        setSelectedInvoice(invoiceData);
        if (invoiceData) {
            const updates = {
                invoice: invoiceData.id,
                account: invoiceData.account,
            };
            // Auto-fill balance as amount
            if (invoiceData.balance && invoiceData.balance > 0) {
                updates.amount = parseFloat(invoiceData.balance);
            }
            form.setFieldsValue(updates);
        } else {
            form.setFieldsValue({ invoice: null });
        }
    };

    // Handle account search
    const onAccountSearch = async (value) => {
        try {
            const response = await adminactions.loadAccounts(1, value, 1000);
            if (response && response.data) {
                setAccounts(response.data.results || response.data || []);
            }
        } catch (err) {
            console.error('Error searching accounts:', err);
        }
    };

    useEffect(() => {
        loadAccounts();
        loadPaymentModes();
        loadPaymentTypes();
        loadPayment();
    }, [id]);

    // Handle pre-filling from query parameters (when coming from invoice)
    useEffect(() => {
        const invoiceId = searchParams.get('invoice_id');
        const accountId = searchParams.get('account_id');

        if (invoiceId && !id) { // Only for new payments
            const loadInvoiceFromParams = async () => {
                try {
                    const invoiceResponse = await adminactions.loadInvoice(invoiceId);
                    if (invoiceResponse) {
                        setSelectedInvoice(invoiceResponse);
                        const updates = {
                            invoice: invoiceResponse.id,
                            account: invoiceResponse.account || accountId,
                            payment_type: 'INVOICE',
                        };
                        // Auto-fill balance as amount
                        if (invoiceResponse.balance && invoiceResponse.balance > 0) {
                            updates.amount = parseFloat(invoiceResponse.balance);
                        }
                        form.setFieldsValue(updates);
                        setSelectedPaymentType('INVOICE');
                    }
                } catch (err) {
                    console.error('Error loading invoice from params:', err);
                }
            };
            loadInvoiceFromParams();
        }
    }, [searchParams, id]);

    // Detect foreign currency and fetch exchange rate when invoice changes
    useEffect(() => {
        // Skip during initial edit load — state is already set from saved payment data
        if (skipInvoiceEffect.current) {
            skipInvoiceEffect.current = false;
            return;
        }

        const checkCurrencyAndFetchRate = async () => {
            if (selectedInvoice && selectedInvoice.currency_data) {
                const currencyCode = selectedInvoice.currency_data.iso_code;
                const isForeign = currencyCode && currencyCode !== 'INR';

                setIsForeignCurrency(isForeign);
                setInvoiceCurrency(selectedInvoice.currency_data);

                if (isForeign) {
                    // Fetch exchange rate from API
                    setExchangeRateLoading(true);
                    try {
                        const rateData = await adminactions.fetchExchangeRate(currencyCode, 'INR');
                        if (rateData && rateData.rate) {
                            setExchangeRate(rateData.rate);
                            form.setFieldsValue({ exchange_rate: rateData.rate });
                        }
                    } catch (err) {
                        console.error('Error fetching exchange rate:', err);
                    } finally {
                        setExchangeRateLoading(false);
                    }

                    // Set original amount to invoice balance
                    if (selectedInvoice.balance && selectedInvoice.balance > 0) {
                        setOriginalAmount(parseFloat(selectedInvoice.balance));
                        form.setFieldsValue({ original_amount: parseFloat(selectedInvoice.balance) });
                    }
                } else {
                    // Reset for INR invoices
                    setExchangeRate(null);
                    setOriginalAmount(null);
                    form.setFieldsValue({ exchange_rate: null, original_amount: null });
                }
            } else {
                setIsForeignCurrency(false);
                setInvoiceCurrency(null);
                setExchangeRate(null);
                setOriginalAmount(null);
            }
        };

        checkCurrencyAndFetchRate();
    }, [selectedInvoice]);

    // Calculate INR amount when original amount or exchange rate changes
    useEffect(() => {
        if (isForeignCurrency && originalAmount && exchangeRate) {
            const inrAmount = parseFloat((originalAmount * exchangeRate).toFixed(2));
            form.setFieldsValue({ amount: inrAmount });
        }
    }, [originalAmount, exchangeRate, isForeignCurrency]);

    // Handle form submission
    const onFinish = async (values) => {
        setSaving(true);
        try {
            const formData = {
                ...values,
                payment_date: values.payment_date ? values.payment_date.format(dateFormat) : null,
                organization: organization,
            };

            // Add multi-currency fields
            if (isForeignCurrency && invoiceCurrency) {
                formData.currency = invoiceCurrency.id;
                formData.original_amount = originalAmount;
                formData.exchange_rate = exchangeRate;
            } else {
                // For INR payments, set exchange_rate to 1 and original_amount = amount
                formData.currency = null;
                formData.original_amount = formData.amount;
                formData.exchange_rate = 1.0;
            }

            // Replace undefined with null
            Object.keys(formData).forEach(key => {
                if (formData[key] === undefined) {
                    formData[key] = null;
                }
            });

            if (isEditMode) {
                const response = await adminactions.updatePayments(formData, id);
                if (response) {
                    // Also persist any edits to the payer details (linked proof)
                    await savePayerDetailsIfChanged();
                    setShowSuccess(true);
                    setTimeout(() => setShowSuccess(false), 3000);
                    message.success('Payment updated successfully!');
                }
            } else {
                const response = await adminactions.addPayments(formData);
                if (response) {
                    message.success('Payment created successfully!');
                    navigate('/payments');
                }
            }
        } catch (err) {
            console.error('Error saving payment:', err);
            message.error('Failed to save payment');
        } finally {
            setSaving(false);
        }
    };

    // View receipt in new tab
    const handleViewReceipt = () => {
        if (!id) return;
        const baseUrl = process.env.REACT_APP_BASE_URL || '';
        window.open(`${baseUrl}invoices/payments/${id}/receiptView/`, '_blank');
    };

    // Download receipt
    const handleDownloadReceipt = async () => {
        if (!id) return;
        setDownloadingReceipt(true);
        try {
            const receiptNo = form.getFieldValue('receipt_no');
            await dispatch(downloadReceipt({ id, receiptNo })).unwrap();
            message.success('Receipt downloaded!');
        } catch (error) {
            message.error('Failed to download receipt');
        } finally {
            setDownloadingReceipt(false);
        }
    };

    if (loading) {
        return (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
                <Spin size="large" tip="Loading payment..." />
            </div>
        );
    }

    return (
        <div style={{ padding: '24px', backgroundColor: '#f5f5f5', minHeight: 'calc(100vh - 64px)' }}>
            {dataSource !== null && (
                <Form
                    form={form}
                    layout="vertical"
                    onFinish={onFinish}
                    initialValues={{
                        ...dataSource,
                        payment_date: dataSource?.payment_date ? dayjs(dataSource.payment_date) : dayjs(),
                        status: dataSource?.status || 'PENDING',
                    }}
                >
                    <Row gutter={24}>
                        {/* Left Column - Main Form */}
                        <Col xs={24} lg={16}>
                            <Card
                                title={
                                    <Space>
                                        <DollarOutlined style={{ color: '#52c41a' }} />
                                        <span>{isEditMode ? 'Edit Payment' : 'Record New Payment'}</span>
                                        {showSuccess && (
                                            <Tag icon={<CheckCircleOutlined />} color="success">
                                                Saved
                                            </Tag>
                                        )}
                                    </Space>
                                }
                                extra={
                                    <Space>
                                        {!isEditMode && (
                                            <Button
                                                icon={<LinkOutlined />}
                                                type="default"
                                                onClick={async () => {
                                                    // Foreign-currency invoice: charge the customer in
                                                    // that currency, not its INR equivalent.
                                                    const foreign = isForeignCurrency && invoiceCurrency?.id;
                                                    const amount = foreign
                                                        ? form.getFieldValue('original_amount')
                                                        : form.getFieldValue('amount');
                                                    if (!amount) {
                                                        message.warning('Please enter an amount first');
                                                        return;
                                                    }
                                                    setPaymentLinkLoading(true);
                                                    setPaymentLinkResult(null);
                                                    try {
                                                        const response = await adminactions.createPaymentLink({
                                                            amount,
                                                            ...(foreign ? { currency: invoiceCurrency.id } : {}),
                                                            account: form.getFieldValue('account') || null,
                                                            description: '',
                                                        });
                                                        if (response.status === 201) {
                                                            setPaymentLinkResult(response.data);
                                                            setPaymentLinkModalOpen(true);
                                                            message.success('Payment link created!');
                                                        }
                                                    } catch (e) {
                                                        const data = e?.response?.data;
                                                        const first = data && typeof data === 'object' ? Object.values(data)[0] : null;
                                                        message.error((Array.isArray(first) ? first[0] : first) || 'Failed to create payment link');
                                                    }
                                                    setPaymentLinkLoading(false);
                                                }}
                                                loading={paymentLinkLoading}
                                            >
                                                Generate Payment Link
                                            </Button>
                                        )}
                                        {isEditMode && !dataSource?.invoice && dataSource?.status === 'PAID' && (
                                            <Button
                                                icon={<FileTextOutlined />}
                                                type="primary"
                                                ghost
                                                onClick={() => navigate(`/invoices/new?payment_id=${id}&account_id=${dataSource?.account || ''}&amount=${dataSource?.amount || ''}`)}
                                            >
                                                Generate Invoice
                                            </Button>
                                        )}
                                        {isEditMode && (
                                            <>
                                                <Button
                                                    icon={<EyeOutlined />}
                                                    onClick={handleViewReceipt}
                                                >
                                                    View Receipt
                                                </Button>
                                                <Button
                                                    icon={<DownloadOutlined />}
                                                    onClick={handleDownloadReceipt}
                                                    loading={downloadingReceipt}
                                                >
                                                    Download Receipt
                                                </Button>
                                            </>
                                        )}
                                        <Button
                                            icon={<CloseOutlined />}
                                            onClick={() => navigate('/payments')}
                                        >
                                            Cancel
                                        </Button>
                                        <Button
                                            type="primary"
                                            icon={<SaveOutlined />}
                                            htmlType="submit"
                                            loading={saving}
                                        >
                                            {isEditMode ? 'Update' : 'Save'} Payment
                                        </Button>
                                    </Space>
                                }
                            >
                                {/* Payment Type & Account Section */}
                                <div style={{ marginBottom: 24 }}>
                                    <Title level={5} style={{ marginBottom: 16, color: '#1890ff' }}>
                                        <CreditCardOutlined style={{ marginRight: 8 }} />
                                        Payment Information
                                    </Title>

                                    <Row gutter={16}>
                                        <Col xs={24} sm={12}>
                                            <Form.Item
                                                label="Payment Type"
                                                name="payment_type"
                                                rules={[{ required: true, message: 'Please select payment type' }]}
                                            >
                                                <Select
                                                    placeholder="Select payment type"
                                                    size="large"
                                                    onChange={(e) => {
                                                        setSelectedPaymentType(e);
                                                        if (e !== 'INVOICE') {
                                                            setSelectedInvoice(null);
                                                            form.setFieldsValue({ invoice: null });
                                                        }
                                                    }}
                                                >
                                                    {paymentTypes.map((item) => (
                                                        <Option key={item[0]} value={item[0]}>
                                                            {item[0]}
                                                        </Option>
                                                    ))}
                                                </Select>
                                            </Form.Item>
                                        </Col>
                                        <Col xs={24} sm={12}>
                                            <Form.Item
                                                label="Account"
                                                name="account"
                                                rules={[{ required: false }]}
                                            >
                                                <Select
                                                    showSearch
                                                    allowClear
                                                    placeholder="Select account"
                                                    size="large"
                                                    optionFilterProp="children"
                                                    onSearch={onAccountSearch}
                                                    filterOption={(input, option) =>
                                                        (option?.children ?? '').toLowerCase().includes(input.toLowerCase())
                                                    }
                                                >
                                                    {accounts.map((item) => (
                                                        <Option key={item.id} value={item.id}>
                                                            {item.name}
                                                        </Option>
                                                    ))}
                                                </Select>
                                            </Form.Item>
                                        </Col>
                                    </Row>

                                    {/* Invoice Selection - Only show when payment type is INVOICE */}
                                    {selectedPaymentType === 'INVOICE' && (
                                        <Form.Item
                                            label={
                                                <Space>
                                                    <FileTextOutlined style={{ color: '#8c8c8c' }} />
                                                    <span>Invoice</span>
                                                </Space>
                                            }
                                            name="invoice"
                                            rules={[{ required: true, message: 'Please select an invoice' }]}
                                        >
                                            <div style={{ width: '100%' }}>
                                                {selectedInvoice ? (
                                                    <div style={{
                                                        padding: '12px 16px',
                                                        border: '1px solid #91d5ff',
                                                        borderRadius: '8px',
                                                        backgroundColor: '#e6f7ff',
                                                        display: 'flex',
                                                        justifyContent: 'space-between',
                                                        alignItems: 'center',
                                                    }}>
                                                        <Space size="middle">
                                                            <FileTextOutlined style={{ fontSize: 20, color: '#1890ff' }} />
                                                            <div>
                                                                <Text strong style={{ fontSize: 15 }}>
                                                                    {selectedInvoice.inv_number}
                                                                </Text>
                                                                <br />
                                                                <Text type="secondary" style={{ fontSize: 12 }}>
                                                                    {selectedInvoice.account_data?.name || 'No account'}
                                                                </Text>
                                                            </div>
                                                            <Divider type="vertical" style={{ height: 40 }} />
                                                            <div>
                                                                <Text type="secondary" style={{ fontSize: 12 }}>Total</Text>
                                                                <br />
                                                                <Text strong>{formatCurrency(selectedInvoice.total)}</Text>
                                                            </div>
                                                            <div>
                                                                <Text type="secondary" style={{ fontSize: 12 }}>Balance</Text>
                                                                <br />
                                                                <Text strong style={{ color: selectedInvoice.balance > 0 ? '#fa8c16' : '#52c41a' }}>
                                                                    {formatCurrency(selectedInvoice.balance)}
                                                                </Text>
                                                            </div>
                                                        </Space>
                                                        <Space>
                                                            <Button size="small" type="link" onClick={() => setInvoiceModalVisible(true)}>
                                                                Change
                                                            </Button>
                                                            <Button
                                                                size="small"
                                                                type="link"
                                                                danger
                                                                icon={<CloseCircleOutlined />}
                                                                onClick={() => {
                                                                    setSelectedInvoice(null);
                                                                    form.setFieldsValue({ invoice: null });
                                                                }}
                                                            />
                                                        </Space>
                                                    </div>
                                                ) : (
                                                    <Button
                                                        type="dashed"
                                                        icon={<FileTextOutlined />}
                                                        onClick={() => setInvoiceModalVisible(true)}
                                                        style={{ width: '100%', height: 50 }}
                                                        size="large"
                                                    >
                                                        Select Invoice
                                                    </Button>
                                                )}
                                            </div>
                                        </Form.Item>
                                    )}
                                </div>

                                <Divider />

                                {/* Amount & Date Section */}
                                <div style={{ marginBottom: 24 }}>
                                    <Title level={5} style={{ marginBottom: 16, color: '#52c41a' }}>
                                        <DollarOutlined style={{ marginRight: 8 }} />
                                        Payment Details
                                        {isForeignCurrency && invoiceCurrency && (
                                            <Tag color="blue" style={{ marginLeft: 8, fontSize: 12 }}>
                                                {invoiceCurrency.iso_code} Invoice
                                            </Tag>
                                        )}
                                    </Title>

                                    {/* Foreign Currency Fields */}
                                    {isForeignCurrency && invoiceCurrency ? (
                                        <>
                                            <Alert
                                                type="info"
                                                showIcon
                                                style={{ marginBottom: 16 }}
                                                message={`This invoice is in ${invoiceCurrency.iso_code}. Enter the payment amount in ${invoiceCurrency.iso_code} and the exchange rate to calculate the INR equivalent.`}
                                            />
                                            <Row gutter={16}>
                                                <Col xs={24} sm={8}>
                                                    <Form.Item
                                                        label={`Amount in ${invoiceCurrency.iso_code}`}
                                                        name="original_amount"
                                                        rules={[{ required: true, message: 'Please enter amount' }]}
                                                    >
                                                        <InputNumber
                                                            style={{ width: '100%' }}
                                                            size="large"
                                                            min={0}
                                                            step={0.01}
                                                            precision={2}
                                                            placeholder="0.00"
                                                            onChange={(value) => setOriginalAmount(value)}
                                                            formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                                                            parser={(value) => value.replace(/,/g, '')}
                                                        />
                                                    </Form.Item>
                                                </Col>
                                                <Col xs={24} sm={8}>
                                                    <Form.Item
                                                        label={
                                                            <Space>
                                                                <span>Exchange Rate (1 {invoiceCurrency.iso_code} = ₹)</span>
                                                                {exchangeRateLoading && <Spin size="small" />}
                                                            </Space>
                                                        }
                                                        name="exchange_rate"
                                                        rules={[{ required: true, message: 'Please enter exchange rate' }]}
                                                    >
                                                        <InputNumber
                                                            style={{ width: '100%' }}
                                                            size="large"
                                                            min={0}
                                                            step={0.0001}
                                                            precision={4}
                                                            placeholder="0.0000"
                                                            onChange={(value) => setExchangeRate(value)}
                                                        />
                                                    </Form.Item>
                                                </Col>
                                                <Col xs={24} sm={8}>
                                                    <Form.Item
                                                        label="INR Amount (Calculated)"
                                                        name="amount"
                                                    >
                                                        <InputNumber
                                                            style={{ width: '100%', backgroundColor: '#f5f5f5' }}
                                                            size="large"
                                                            disabled
                                                            prefix="₹"
                                                            precision={2}
                                                            formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                                                        />
                                                    </Form.Item>
                                                </Col>
                                            </Row>
                                        </>
                                    ) : (
                                        /* INR Amount Field (Original Behavior) */
                                        <Row gutter={16}>
                                            <Col xs={24} sm={8}>
                                                <Form.Item
                                                    label="Amount Received"
                                                    name="amount"
                                                    rules={[{ required: true, message: 'Please enter amount' }]}
                                                >
                                                    <InputNumber
                                                        style={{ width: '100%' }}
                                                        size="large"
                                                        min={0}
                                                        step={0.01}
                                                        precision={2}
                                                        prefix="₹"
                                                        placeholder="0.00"
                                                        formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                                                        parser={(value) => value.replace(/₹\s?|(,*)/g, '')}
                                                    />
                                                </Form.Item>
                                            </Col>
                                        </Row>
                                    )}

                                    <Row gutter={16}>
                                        <Col xs={24} sm={8}>
                                            <Form.Item
                                                label="Payment Date"
                                                name="payment_date"
                                                rules={[{ required: true, message: 'Please select date' }]}
                                            >
                                                <DatePicker
                                                    style={{ width: '100%' }}
                                                    size="large"
                                                    format="YYYY-MM-DD"
                                                />
                                            </Form.Item>
                                        </Col>
                                        <Col xs={24} sm={8}>
                                            <Form.Item
                                                label="Payment Mode"
                                                name="payment_mode"
                                                rules={[{ required: true, message: 'Please select payment mode' }]}
                                            >
                                                <Select
                                                    placeholder="Select mode"
                                                    size="large"
                                                >
                                                    {paymentModes.map((item) => (
                                                        <Option key={item[0]} value={item[0]}>
                                                            {item[0]}
                                                        </Option>
                                                    ))}
                                                </Select>
                                            </Form.Item>
                                        </Col>
                                        <Col xs={24} sm={8}>
                                            <Form.Item
                                                label="Transaction Reference"
                                                name="transaction_ref"
                                            >
                                                <Input
                                                    size="large"
                                                    placeholder="Enter transaction reference number"
                                                />
                                            </Form.Item>
                                        </Col>
                                    </Row>

                                    <Row gutter={16}>
                                        <Col xs={24} sm={8}>
                                            <Form.Item
                                                label="Transaction Order Ref ID"
                                                name="transaction_id"
                                                tooltip="Order/transaction reference from the payment gateway"
                                            >
                                                <Input
                                                    size="large"
                                                    placeholder="Enter transaction order ref id"
                                                />
                                            </Form.Item>
                                        </Col>
                                        <Col xs={24} sm={8}>
                                            <Form.Item
                                                label="Receipt No"
                                                name="receipt_no"
                                                tooltip="Auto-generated on save. You can override with a custom value."
                                            >
                                                <Input
                                                    size="large"
                                                    placeholder="Auto-generated (e.g. PR2025000001)"
                                                />
                                            </Form.Item>
                                        </Col>
                                        <Col xs={24} sm={8}>
                                            <Form.Item
                                                label="Status"
                                                name="status"
                                                rules={[{ required: true, message: 'Please select status' }]}
                                            >
                                                <Select placeholder="Select status" size="large">
                                                    <Option value="PENDING">
                                                        <Tag color="orange">Pending</Tag>
                                                    </Option>
                                                    <Option value="PAID">
                                                        <Tag color="green">Paid</Tag>
                                                    </Option>
                                                    <Option value="REFUNDED">
                                                        <Tag color="purple">Refunded</Tag>
                                                    </Option>
                                                    <Option value="FAILED">
                                                        <Tag color="red">Failed</Tag>
                                                    </Option>
                                                </Select>
                                            </Form.Item>
                                        </Col>
                                    </Row>
                                </div>

                                <Divider />

                                {/* Attached Payment Link — shown whenever this payment was collected through one */}
                                {dataSource?.payment_link_data && (
                                    <>
                                        <div style={{ marginBottom: 24 }}>
                                            <Title level={5} style={{ marginBottom: 16, color: '#1677ff' }}>
                                                <LinkOutlined style={{ marginRight: 8 }} />
                                                Payment Link
                                            </Title>
                                            <Row gutter={16}>
                                                <Col xs={24} sm={8}>
                                                    <Text type="secondary">Description</Text>
                                                    <div>
                                                        <Text strong>
                                                            {dataSource.payment_link_data.description
                                                                || dataSource.payment_link_data.customer_name
                                                                || `Link ${String(dataSource.payment_link_data.token || '').slice(0, 8)}`}
                                                        </Text>
                                                    </div>
                                                </Col>
                                                <Col xs={24} sm={4}>
                                                    <Text type="secondary">Link Amount</Text>
                                                    <div>
                                                        <Text strong>
                                                            {dataSource.payment_link_data.amount != null
                                                                ? formatMoney(dataSource.payment_link_data.amount, dataSource.payment_link_data.currency_code || 'INR')
                                                                : '-'}
                                                        </Text>
                                                    </div>
                                                </Col>
                                                <Col xs={24} sm={4}>
                                                    <Text type="secondary">Link Status</Text>
                                                    <div>
                                                        <Tag color={dataSource.payment_link_data.status === 'PAID' ? 'green' : 'blue'}>
                                                            {dataSource.payment_link_data.status}
                                                        </Tag>
                                                    </div>
                                                </Col>
                                                <Col xs={24} sm={8}>
                                                    <Space wrap style={{ marginTop: 20 }}>
                                                        <Button
                                                            icon={<CopyOutlined />}
                                                            onClick={() => {
                                                                const url = dataSource.payment_link_data.payment_link_url;
                                                                if (!url) return;
                                                                copyText(url).then((ok) => {
                                                                    if (!ok) return message.error("Couldn't copy. Please copy the link manually.");
                                                                    message.success('Payment link copied to clipboard!');
                                                                });
                                                            }}
                                                            disabled={!dataSource.payment_link_data.payment_link_url}
                                                        >
                                                            Copy Link
                                                        </Button>
                                                        <Button
                                                            icon={<DollarOutlined />}
                                                            onClick={() => navigate(`/payments?payment_link=${dataSource.payment_link_data.id}`)}
                                                        >
                                                            All Payments of This Link
                                                        </Button>
                                                    </Space>
                                                </Col>
                                            </Row>
                                        </div>
                                        <Divider />
                                    </>
                                )}

                                {/* Payer Details Section — only when payment came from a multi-payment link */}
                                {dataSource?.payer_details && (
                                    <>
                                        <div style={{ marginBottom: 24 }}>
                                            <Title level={5} style={{ marginBottom: 16, color: '#13c2c2' }}>
                                                <UserOutlined style={{ marginRight: 8 }} />
                                                Payer Details
                                            </Title>
                                            <Alert
                                                type="info"
                                                showIcon
                                                message="Submitted by the payer on a multi-payment link. You can correct these details below."
                                                style={{ marginBottom: 16 }}
                                            />
                                            <Row gutter={16}>
                                                <Col xs={24} sm={8}>
                                                    <Form.Item label="Name" required>
                                                        <Input
                                                            value={payerEdit.name}
                                                            onChange={(e) => setPayerEdit((p) => ({ ...p, name: e.target.value }))}
                                                            placeholder="Payer name"
                                                            size="large"
                                                        />
                                                    </Form.Item>
                                                </Col>
                                                <Col xs={24} sm={8}>
                                                    <Form.Item label="Email">
                                                        <Input
                                                            type="email"
                                                            value={payerEdit.email}
                                                            onChange={(e) => setPayerEdit((p) => ({ ...p, email: e.target.value }))}
                                                            placeholder="Payer email"
                                                            size="large"
                                                        />
                                                    </Form.Item>
                                                </Col>
                                                <Col xs={24} sm={8}>
                                                    <Form.Item label="Phone">
                                                        <Input
                                                            value={payerEdit.phone}
                                                            onChange={(e) => setPayerEdit((p) => ({ ...p, phone: e.target.value }))}
                                                            placeholder="Payer phone"
                                                            maxLength={20}
                                                            size="large"
                                                        />
                                                    </Form.Item>
                                                </Col>
                                            </Row>
                                        </div>
                                        <Divider />
                                    </>
                                )}

                                {/* Bank Transaction Section */}
                                <div style={{ marginBottom: 24 }}>
                                    <Title level={5} style={{ marginBottom: 16, color: '#722ed1' }}>
                                        <BankOutlined style={{ marginRight: 8 }} />
                                        Bank Transaction Reference
                                    </Title>

                                    <Form.Item name="bank_transaction">
                                        <div style={{ width: '100%' }}>
                                            {selectedBankTxn ? (
                                                <div style={{
                                                    padding: '12px 16px',
                                                    border: '1px solid #b7eb8f',
                                                    borderRadius: '8px',
                                                    backgroundColor: '#f6ffed',
                                                    display: 'flex',
                                                    justifyContent: 'space-between',
                                                    alignItems: 'center',
                                                }}>
                                                    <Space size="middle">
                                                        <BankOutlined style={{
                                                            fontSize: 20,
                                                            color: selectedBankTxn.transaction_type === 'DEPOSIT' ? '#52c41a' : '#f5222d'
                                                        }} />
                                                        <div>
                                                            <Text strong>
                                                                {dayjs(selectedBankTxn.transaction_date).format('DD/MM/YYYY')}
                                                            </Text>
                                                            <br />
                                                            <Text type="secondary" style={{ fontSize: 12 }}>
                                                                {selectedBankTxn.remarks?.substring(0, 40) || 'No remarks'}
                                                                {selectedBankTxn.remarks?.length > 40 ? '...' : ''}
                                                            </Text>
                                                        </div>
                                                        <Tag color={selectedBankTxn.transaction_type === 'DEPOSIT' ? 'green' : 'red'} style={{ fontSize: 14 }}>
                                                            {selectedBankTxn.transaction_type === 'DEPOSIT' ? '+' : '-'}
                                                            {formatCurrency(selectedBankTxn.amount, 'INR')}
                                                        </Tag>
                                                    </Space>
                                                    <Space>
                                                        <Button size="small" type="link" onClick={() => setBankTxnModalVisible(true)}>
                                                            Change
                                                        </Button>
                                                        <Button
                                                            size="small"
                                                            type="link"
                                                            danger
                                                            icon={<CloseCircleOutlined />}
                                                            onClick={() => {
                                                                setSelectedBankTxn(null);
                                                                form.setFieldsValue({ bank_transaction: null });
                                                            }}
                                                        />
                                                    </Space>
                                                </div>
                                            ) : (
                                                <Button
                                                    type="dashed"
                                                    icon={<LinkOutlined />}
                                                    onClick={() => setBankTxnModalVisible(true)}
                                                    style={{ width: '100%', height: 50 }}
                                                    size="large"
                                                >
                                                    Link Bank Transaction
                                                </Button>
                                            )}
                                        </div>
                                    </Form.Item>
                                </div>

                                <Divider />

                                {/* Notes Section */}
                                <div>
                                    <Title level={5} style={{ marginBottom: 16, color: '#8c8c8c' }}>
                                        <FileTextOutlined style={{ marginRight: 8 }} />
                                        Additional Information
                                    </Title>

                                    <Form.Item label="Private Notes" name="private_notes">
                                        <TextArea
                                            rows={3}
                                            placeholder="Internal notes (not visible to customer)"
                                        />
                                    </Form.Item>

                                    <Form.Item
                                        label="Send Email Notification"
                                        name="email"
                                        valuePropName="checked"
                                    >
                                        <Switch
                                            checkedChildren={<SendOutlined />}
                                            unCheckedChildren={<CloseOutlined />}
                                        />
                                    </Form.Item>
                                </div>
                            </Card>
                        </Col>

                        {/* Right Column - Summary */}
                        <Col xs={24} lg={8}>
                            <Card title="Payment Summary" style={{ marginBottom: 16 }}>
                                <Space direction="vertical" style={{ width: '100%' }} size="middle">
                                    {/* Show foreign currency amount if applicable */}
                                    {isForeignCurrency && invoiceCurrency && originalAmount && (
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                            <Text type="secondary">Original ({invoiceCurrency.iso_code}):</Text>
                                            <Text strong style={{ fontSize: 16 }}>
                                                {invoiceCurrency.iso_code} {originalAmount?.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                                            </Text>
                                        </div>
                                    )}

                                    {isForeignCurrency && exchangeRate && (
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                            <Text type="secondary">Exchange Rate:</Text>
                                            <Text>1 {invoiceCurrency?.iso_code} = ₹{exchangeRate?.toFixed(4)}</Text>
                                        </div>
                                    )}

                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <Text type="secondary">{isForeignCurrency ? 'INR Amount:' : 'Amount:'}</Text>
                                        <Text strong style={{ fontSize: 20, color: '#52c41a' }}>
                                            {formatCurrency(form.getFieldValue('amount'), 'INR')}
                                        </Text>
                                    </div>

                                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                        <Text type="secondary">Status:</Text>
                                        {(() => {
                                            const status = form.getFieldValue('status');
                                            const config = STATUS_CONFIG[status] || { color: 'default' };
                                            return (
                                                <Tag color={config.color} icon={config.icon}>
                                                    {status || 'Not set'}
                                                </Tag>
                                            );
                                        })()}
                                    </div>

                                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                        <Text type="secondary">Date:</Text>
                                        <Text>
                                            {form.getFieldValue('payment_date')
                                                ? dayjs(form.getFieldValue('payment_date')).format('MMM DD, YYYY')
                                                : 'Not selected'}
                                        </Text>
                                    </div>

                                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                        <Text type="secondary">Payment Type:</Text>
                                        <Text>{selectedPaymentType || 'Not selected'}</Text>
                                    </div>

                                    <Divider style={{ margin: '12px 0' }} />

                                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                        <Text type="secondary">Invoice Linked:</Text>
                                        <Tag color={selectedInvoice ? 'blue' : 'default'}>
                                            {selectedInvoice ? selectedInvoice.inv_number : 'None'}
                                        </Tag>
                                    </div>

                                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                        <Text type="secondary">Bank Txn Linked:</Text>
                                        <Tag color={selectedBankTxn ? 'green' : 'default'}>
                                            {selectedBankTxn ? 'Yes' : 'None'}
                                        </Tag>
                                    </div>
                                </Space>
                            </Card>

                            {/* Invoice Details Card */}
                            {selectedInvoice && (
                                <Card
                                    title={
                                        <Space>
                                            <FileTextOutlined style={{ color: '#1890ff' }} />
                                            Invoice Details
                                            {isForeignCurrency && invoiceCurrency && (
                                                <Tag color="blue" style={{ marginLeft: 4 }}>
                                                    {invoiceCurrency.iso_code}
                                                </Tag>
                                            )}
                                        </Space>
                                    }
                                    size="small"
                                    style={{ marginBottom: 16 }}
                                >
                                    <Space direction="vertical" style={{ width: '100%' }}>
                                        <Statistic
                                            title="Invoice Number"
                                            value={selectedInvoice.inv_number}
                                            valueStyle={{ fontSize: 16 }}
                                        />
                                        <Row gutter={16}>
                                            <Col span={12}>
                                                <Statistic
                                                    title="Total"
                                                    value={selectedInvoice.total || 0}
                                                    prefix={isForeignCurrency && invoiceCurrency ? `${invoiceCurrency.iso_code} ` : '₹'}
                                                    precision={2}
                                                    valueStyle={{ fontSize: 14 }}
                                                />
                                            </Col>
                                            <Col span={12}>
                                                <Statistic
                                                    title="Balance"
                                                    value={selectedInvoice.balance || 0}
                                                    prefix={isForeignCurrency && invoiceCurrency ? `${invoiceCurrency.iso_code} ` : '₹'}
                                                    precision={2}
                                                    valueStyle={{
                                                        fontSize: 14,
                                                        color: selectedInvoice.balance > 0 ? '#fa8c16' : '#52c41a'
                                                    }}
                                                />
                                            </Col>
                                        </Row>
                                    </Space>
                                </Card>
                            )}

                            {/* Receipt Actions Card - Only in Edit Mode */}
                            {isEditMode && (
                                <Card
                                    title={
                                        <Space>
                                            <FilePdfOutlined style={{ color: '#52c41a' }} />
                                            Payment Receipt
                                        </Space>
                                    }
                                    size="small"
                                    style={{ marginBottom: 16 }}
                                >
                                    <Space direction="vertical" style={{ width: '100%' }}>
                                        {dataSource?.receipt_no && (
                                            <div style={{ padding: '8px 12px', background: '#f6ffed', border: '1px solid #b7eb8f', borderRadius: 6, textAlign: 'center' }}>
                                                <span style={{ fontSize: 11, color: '#888' }}>Receipt No</span>
                                                <div style={{ fontFamily: 'monospace', fontWeight: 600, fontSize: 15 }}>{dataSource.receipt_no}</div>
                                            </div>
                                        )}
                                        <Button
                                            icon={<EyeOutlined />}
                                            onClick={handleViewReceipt}
                                            block
                                        >
                                            View Receipt
                                        </Button>
                                        <Button
                                            icon={<DownloadOutlined />}
                                            onClick={handleDownloadReceipt}
                                            loading={downloadingReceipt}
                                            block
                                        >
                                            Download Receipt
                                        </Button>
                                    </Space>
                                </Card>
                            )}

                            {/* Tips Card */}
                            <Card
                                size="small"
                                style={{ backgroundColor: '#f0f5ff', border: '1px solid #adc6ff' }}
                            >
                                <Title level={5} style={{ color: '#2f54eb', marginBottom: 8 }}>
                                    Quick Tips
                                </Title>
                                <ul style={{ color: '#1d39c4', paddingLeft: 20, margin: 0, fontSize: 13 }}>
                                    <li>Link bank transactions to auto-fill amount & date</li>
                                    <li>Select invoice to auto-populate account & balance</li>
                                    <li>Transaction reference helps with reconciliation</li>
                                    <li>Enable email to notify customer of payment</li>
                                </ul>
                            </Card>
                        </Col>
                    </Row>
                </Form>
            )}

            {/* Modals */}
            <BankTransactionSelectModal
                visible={bankTxnModalVisible}
                onClose={() => setBankTxnModalVisible(false)}
                onSelect={handleBankTxnSelect}
                selectedTransactionId={selectedBankTxn?.id}
                title="Select Bank Transaction for Payment"
            />

            <InvoiceSelectModal
                visible={invoiceModalVisible}
                onClose={() => setInvoiceModalVisible(false)}
                onSelect={handleInvoiceSelect}
                selectedInvoiceId={selectedInvoice?.id}
                accountId={form.getFieldValue('account')}
                title="Select Invoice for Payment"
            />

            {/* Payment Link Result Modal */}
            <Modal
                title="Payment Link Created"
                open={paymentLinkModalOpen}
                onCancel={() => { setPaymentLinkModalOpen(false); setPaymentLinkResult(null); }}
                footer={[
                    <Button key="close" onClick={() => { setPaymentLinkModalOpen(false); setPaymentLinkResult(null); }}>
                        Close
                    </Button>,
                    <Button
                        key="copy"
                        type="primary"
                        icon={<CopyOutlined />}
                        onClick={() => {
                            if (paymentLinkResult?.payment_link_url) {
                                copyText(paymentLinkResult.payment_link_url).then((ok) => {
                                    if (!ok) return message.error("Couldn't copy. Please copy the link manually.");
                                    message.success("Payment link copied to clipboard!");
                                });
                            }
                        }}
                    >
                        Copy Link
                    </Button>,
                ]}
            >
                {paymentLinkResult && (
                    <div>
                        <p style={{ marginBottom: 12, color: '#52c41a', fontWeight: 500 }}>Payment link created successfully!</p>
                        <Input
                            value={paymentLinkResult.payment_link_url}
                            readOnly
                            addonAfter={
                                <CopyOutlined
                                    onClick={() => {
                                        copyText(paymentLinkResult.payment_link_url).then((ok) => {
                                            if (!ok) return message.error("Couldn't copy. Please copy the link manually.");
                                            message.success("Copied!");
                                        });
                                    }}
                                    style={{ cursor: 'pointer' }}
                                />
                            }
                        />
                        <div style={{ marginTop: 12, fontSize: 12, color: '#888' }}>
                            Amount: {formatMoney(paymentLinkResult.amount, paymentLinkResult.currency_data?.iso_code)}
                        </div>
                    </div>
                )}
            </Modal>
        </div>
    );
};

export default PaymentForm;
